import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Sparkle, ArrowRight, ChartLineUp, Lightning, Users } from "@phosphor-icons/react";
import { AIOutput, ChipSelector } from "@/components/shared";
import { API } from "@/lib/api";

const FALLBACK_TOPICS = [
  "The AI tool that changed how I work",
  "What I learned from my biggest business failure",
  "Why I stopped chasing vanity metrics",
  "The mindset that 10x'd my productivity",
  "What successful people do differently",
  "The morning routine that actually works",
  "The framework I use for every big decision",
  "What nobody tells you about building a personal brand",
  "The skill that matters more than any degree",
  "How I went from employee to entrepreneur",
];

const LinkedInEngine = () => {
  const [formats, setFormats] = useState([]);
  const [activeTab, setActiveTab] = useState("single");
  const [personalizedTopics, setPersonalizedTopics] = useState([]);
  const [form, setForm] = useState({
    format_type: "story",
    topic: "",
    tone: "thought-leader",
    length: "medium",
  });
  const [weekContext, setWeekContext] = useState("");
  const [output, setOutput] = useState(null);
  const [topicIdeas, setTopicIdeas] = useState(null);
  const [loading, setLoading] = useState(false);

  const tones = ["thought-leader", "vulnerable", "bold", "warm"];
  const lengths = ["short", "medium", "long"];

  useEffect(() => {
    const fetchFormats = async () => {
      try {
        const res = await axios.get(`${API}/linkedin/formats`);
        setFormats(res.data.formats);
      } catch (e) {
        console.error("Error fetching formats:", e);
      }
    };
    const fetchTopics = async () => {
      try {
        const res = await axios.get(`${API}/topics/personalized`);
        // API returns [{topic, used, source}, ...] — extract strings so they
        // render correctly in <option>. Falling through to FALLBACK_TOPICS if
        // the request fails or the list is empty.
        const items = res.data?.topics || [];
        const strs = items.map((t) => (typeof t === "string" ? t : t?.topic)).filter(Boolean);
        setPersonalizedTopics(strs.length ? strs : FALLBACK_TOPICS);
      } catch (e) {
        setPersonalizedTopics(FALLBACK_TOPICS);
      }
    };
    fetchFormats();
    fetchTopics();
  }, []);

  // Generic async polling helper
  const pollTask = async (taskId, onComplete) => {
    const maxAttempts = 30;
    for (let i = 0; i < maxAttempts; i++) {
      await new Promise(r => setTimeout(r, 2000));
      try {
        const res = await axios.get(`${API}/linkedin/task/${taskId}`);
        if (res.data.status === "completed") {
          onComplete(res.data);
          return;
        } else if (res.data.status === "failed") {
          console.error("Task failed:", res.data.error);
          return;
        }
      } catch (e) { console.warn("Poll error, retrying:", e); }
    }
  };

  const generatePost = async () => {
    if (!form.topic) return;
    setLoading(true);
    try {
      const res = await axios.post(`${API}/linkedin/generate`, form);
      if (res.data.task_id) {
        await pollTask(res.data.task_id, (data) => {
          setOutput(data.result);
        });
      }
    } catch (e) {
      console.error("Error generating post:", e);
    } finally {
      setLoading(false);
    }
  };

  const generateTopicIdeas = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/linkedin/topic-ideas`);
      if (res.data.task_id) {
        await pollTask(res.data.task_id, (data) => {
          setTopicIdeas(data.result);
        });
      }
    } catch (e) {
      console.error("Error generating topics:", e);
    } finally {
      setLoading(false);
    }
  };

  const generateWeek = async () => {
    if (!weekContext) return;
    setLoading(true);
    try {
      const res = await axios.post(`${API}/linkedin/full-week`, { context: weekContext });
      if (res.data.task_id) {
        await pollTask(res.data.task_id, (data) => {
          setOutput(data.result);
        });
      }
    } catch (e) {
      console.error("Error generating week:", e);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-zinc-900">LinkedIn Authority Engine</h2>
        <p className="text-zinc-500 mt-1">Build authority with strategic LinkedIn posts</p>
      </div>

      {/* ────────────────────────────────────────────────────────────
          Partner Spotlight — Lead Leap
          The user creates content here; Lead Leap turns that content
          into booked calls + revenue. Surfaced inside LinkedIn Engine
          because LinkedIn outreach is the highest-intent conversion
          surface for the partnership.
          ──────────────────────────────────────────────────────────── */}
      <a
        href="https://leadleap.io/branding-bar"
        target="_blank"
        rel="noopener noreferrer"
        data-testid="leadleap-partner-card"
        className="group block relative overflow-hidden rounded-2xl border border-[#E91E8C]/30 bg-gradient-to-br from-[#E91E8C] via-[#C8186F] to-[#1a0a14] hover:from-[#FF1F9C] hover:via-[#D71E80] transition-all shadow-lg hover:shadow-2xl hover:shadow-[#E91E8C]/30"
      >
        {/* Decorative noise / grain layer */}
        <div className="absolute inset-0 opacity-20 mix-blend-overlay pointer-events-none"
          style={{
            backgroundImage: "radial-gradient(circle at 20% 20%, rgba(255,255,255,0.4) 0%, transparent 50%), radial-gradient(circle at 80% 80%, rgba(255,255,255,0.3) 0%, transparent 50%)",
          }}
        />

        <div className="relative grid md:grid-cols-[1.2fr_1fr] gap-6 p-5 md:p-7">
          {/* LEFT — Pitch column */}
          <div className="flex flex-col justify-center text-white space-y-4">
            <div className="flex items-center gap-2">
              <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-white/15 backdrop-blur-sm text-[10px] font-bold uppercase tracking-widest">
                <Sparkle size={11} weight="fill" /> Partner Spotlight
              </span>
              <span className="text-[10px] text-white/70 font-semibold uppercase tracking-widest">The Branding Bar × Lead Leap</span>
            </div>

            <div>
              <h3 className="text-2xl md:text-3xl font-extrabold leading-tight tracking-tight">
                Posting isn&apos;t the goal.<br />
                <span className="text-white/90">Converting is.</span>
              </h3>
              <p className="text-sm text-white/80 mt-2 max-w-md leading-relaxed">
                You&apos;re already creating high-converting content here. Lead Leap plugs in a multi-channel outreach + follow-up engine that turns those LinkedIn views into booked calls, qualified pipeline, and closed clients.
              </p>
            </div>

            <div className="grid grid-cols-3 gap-2 max-w-sm">
              <div className="flex flex-col gap-1">
                <Lightning size={16} weight="fill" className="text-white" />
                <p className="text-[11px] text-white/90 leading-tight font-semibold">Multi-channel outreach</p>
                <p className="text-[9px] text-white/60 leading-tight">LinkedIn · Email · SMS</p>
              </div>
              <div className="flex flex-col gap-1">
                <ChartLineUp size={16} weight="fill" className="text-white" />
                <p className="text-[11px] text-white/90 leading-tight font-semibold">Smart follow-up</p>
                <p className="text-[9px] text-white/60 leading-tight">Pipeline + CRM visibility</p>
              </div>
              <div className="flex flex-col gap-1">
                <Users size={16} weight="fill" className="text-white" />
                <p className="text-[11px] text-white/90 leading-tight font-semibold">500+ creators</p>
                <p className="text-[9px] text-white/60 leading-tight">Already inside</p>
              </div>
            </div>

            <div className="flex items-center gap-3 pt-1">
              <span
                data-testid="leadleap-cta"
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-white text-[#E91E8C] font-bold text-sm group-hover:gap-3 group-hover:bg-zinc-50 transition-all shadow-md"
              >
                Activate My Revenue Engine
                <ArrowRight size={16} weight="bold" className="transition-transform group-hover:translate-x-0.5" />
              </span>
              <span className="text-[11px] text-white/60">leadleap.io/branding-bar</span>
            </div>
          </div>

          {/* RIGHT — Landing-page mini preview */}
          <div className="relative hidden md:block">
            <div className="relative rounded-xl bg-white/95 backdrop-blur-sm shadow-2xl overflow-hidden border border-white/40 rotate-1 group-hover:rotate-0 transition-transform duration-500">
              {/* Browser chrome */}
              <div className="flex items-center gap-1.5 px-3 py-2 bg-zinc-100 border-b border-zinc-200">
                <span className="w-2 h-2 rounded-full bg-red-400" />
                <span className="w-2 h-2 rounded-full bg-amber-400" />
                <span className="w-2 h-2 rounded-full bg-green-400" />
                <span className="ml-2 flex-1 text-[9px] text-zinc-500 font-mono truncate">leadleap.io/branding-bar</span>
              </div>
              <div className="p-4 space-y-3 bg-gradient-to-br from-zinc-50 via-white to-pink-50">
                <span className="inline-block text-[8px] font-bold uppercase tracking-widest text-[#E91E8C] bg-[#E91E8C]/10 px-2 py-0.5 rounded-full">
                  AI Content × Revenue Engine
                </span>
                <h4 className="text-base font-extrabold text-zinc-900 leading-tight">
                  Build Your AI Twin.<br />
                  Turn It Into Real Revenue.
                </h4>
                <p className="text-[10px] text-zinc-600 leading-snug">
                  Create high-converting content with your AI avatar — then plug into a system that turns visibility into booked calls and consistent pipeline.
                </p>
                <div className="flex items-center gap-1.5 pt-1">
                  <div className="flex -space-x-1.5">
                    {["JT","MD","AR","DW"].map((init, i) => (
                      <span key={init} className={`w-5 h-5 rounded-full text-[8px] font-bold text-white flex items-center justify-center border border-white ${["bg-[#E91E8C]","bg-violet-500","bg-amber-500","bg-emerald-500"][i]}`}>
                        {init}
                      </span>
                    ))}
                  </div>
                  <p className="text-[9px] text-zinc-600 font-semibold"><span className="text-[#E91E8C] font-extrabold">500+</span> creators already inside</p>
                </div>
                <div className="grid grid-cols-2 gap-1.5 pt-1">
                  <span className="text-[8px] bg-zinc-900 text-white px-2 py-1.5 rounded text-center font-bold">Get Started Free</span>
                  <span className="text-[8px] border border-zinc-300 px-2 py-1.5 rounded text-center font-bold text-zinc-700">Book Strategy Call</span>
                </div>
              </div>
            </div>
            {/* Glow */}
            <div className="absolute -inset-2 bg-gradient-to-br from-white/20 to-transparent rounded-2xl blur-2xl -z-10 pointer-events-none" />
          </div>
        </div>
      </a>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-zinc-200">
        {[
          { id: "single", label: "Write a Post" },
          { id: "week", label: "Full Week" },
          { id: "guide", label: "Format Guide" },
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2 font-medium text-sm border-b-2 transition-all ${
              activeTab === tab.id
                ? "border-[#E91E8C] text-[#E91E8C]"
                : "border-transparent text-zinc-500 hover:text-zinc-700"
            }`}
            data-testid={`linkedin-tab-${tab.id}`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === "single" && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="space-y-6">
            {/* Format Selection */}
            <div className="card p-4 sm:p-6">
              <label className="text-xs font-bold uppercase tracking-wider text-zinc-500 block mb-3">
                Post Format
              </label>
              <div className="grid grid-cols-2 gap-2">
                {formats.map((f) => (
                  <button
                    key={f.id}
                    onClick={() => setForm({ ...form, format_type: f.id })}
                    className={`p-3 rounded-lg text-left transition-all ${
                      form.format_type === f.id
                        ? "bg-[#E91E8C]/10 border-2 border-[#E91E8C]"
                        : "bg-zinc-50 border-2 border-transparent hover:border-zinc-200"
                    }`}
                    data-testid={`format-${f.id}`}
                  >
                    <div className="font-medium text-sm text-zinc-900">{f.name}</div>
                    <div className="text-xs text-zinc-500 mt-0.5">{f.description}</div>
                  </button>
                ))}
              </div>
            </div>

            <div className="card p-4 sm:p-6 space-y-4">
              <div className="flex items-center justify-between">
                <label className="text-xs font-bold uppercase tracking-wider text-zinc-500">Topic</label>
                <button
                  onClick={generateTopicIdeas}
                  className="text-sm text-[#E91E8C] hover:underline flex items-center gap-1"
                  data-testid="get-topic-ideas-btn"
                >
                  <Sparkle size={14} /> Get AI Ideas
                </button>
              </div>
              <select
                value={(personalizedTopics.length > 0 ? personalizedTopics : FALLBACK_TOPICS).includes(form.topic) ? form.topic : ""}
                onChange={(e) => setForm({ ...form, topic: e.target.value })}
                className="input-field"
                data-testid="linkedin-topic-select"
              >
                <option value="">Choose a topic or write your own below...</option>
                {(personalizedTopics.length > 0 ? personalizedTopics : FALLBACK_TOPICS).map((topic, i) => (
                  <option key={i} value={topic}>
                    {topic}
                  </option>
                ))}
              </select>
              <textarea
                value={form.topic}
                onChange={(e) => setForm({ ...form, topic: e.target.value })}
                className="textarea-field h-20"
                placeholder="Or type your own topic here..."
                data-testid="linkedin-topic-input"
              />

              <ChipSelector label="Tone" options={tones} selected={form.tone} onChange={(v) => setForm({ ...form, tone: v })} />
              <ChipSelector label="Length" options={lengths} selected={form.length} onChange={(v) => setForm({ ...form, length: v })} />

              <button
                onClick={generatePost}
                disabled={!form.topic || loading}
                className="btn-primary w-full"
                data-testid="generate-linkedin-btn"
              >
                {loading ? "Generating..." : "Generate Post"}
              </button>
            </div>

            {topicIdeas && (
              <div className="card p-4">
                <h4 className="font-semibold text-zinc-900 mb-2">Topic Ideas</h4>
                <div className="text-sm text-zinc-700 whitespace-pre-wrap">{topicIdeas}</div>
              </div>
            )}
          </div>

          <div>
            <AIOutput content={output} loading={loading} label="Your LinkedIn Post" />
          </div>
        </div>
      )}

      {activeTab === "week" && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="card p-4 sm:p-6 space-y-4">
            <label className="text-xs font-bold uppercase tracking-wider text-zinc-500">Week Context</label>
            <textarea
              value={weekContext}
              onChange={(e) => setWeekContext(e.target.value)}
              className="textarea-field h-32"
              placeholder="What themes or topics should this week's content focus on?"
              data-testid="week-context-input"
            />
            <button onClick={generateWeek} disabled={!weekContext || loading} className="btn-primary w-full" data-testid="generate-week-btn">
              {loading ? "Generating..." : "Generate Mon/Wed/Fri"}
            </button>
          </div>
          <AIOutput content={output} loading={loading} label="Your Week of Content" />
        </div>
      )}

      {activeTab === "guide" && (
        <div className="card p-4 sm:p-6">
          <h4 className="font-semibold text-zinc-900 mb-4">LinkedIn Post Format Guide</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {formats.map((f) => (
              <div key={f.id} className="bg-zinc-50 rounded-lg p-4">
                <h5 className="font-semibold text-zinc-900">{f.name}</h5>
                <p className="text-sm text-zinc-600 mt-1">{f.description}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default LinkedInEngine;
